import React, { FC } from 'react'
import { Tooltip, Card, Typography, Box, Button } from '@mui/material'
import { Alert } from '@mui/material'
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid'
import ListVehicleCell from '../vehicle-detail-cells/list-vehicle-cell'
import { ListDelistModal } from '../list-delist-modal/listDelistModal'
import FilterButton from '../filter/FilterButton'
import VehicleDetailCell from '../vehicle-detail-cells/vehicle-detail-cell'
import { useVehiclesTable } from './useVehiclesTable'
import VehicleMakeSelectField from './VehicleMakeSelectField'
import { FormDrawerComponent } from 'shared/FormDrawerComponent'
import { AddVehicleForm } from './AddVehicleForm'
import { UploadMultipleVehiclesForm } from './UploadMultipleVehiclesForm'

export const VehiclesTable: FC = () => {
  const {
    tabs,
    vehicles,
    selectTab,
    activeTab,
    vehicleToList,
    onListVehicleClose,
    vehicleQuery,
    fetchListVehiclesError,
    listVehicle,
    listDelistMutation,
    total,
    setPaginationModel,
    query,
    setQuery,
    addVehicleMutation,
    handleAddVehicle,
    isAddVehicleDrawerOpen,
    setIsAddVehicleDrawerOpen,
    addMultipleVehiclesMutation,
    handleUploadMultipleVehicles,
    isUploadMultipleDrawerOpen,
    setIsUploadMultipleDrawerOpen,
  } = useVehiclesTable()

  const isHidden = () => {
    return (
      tabs.ASSIGNED.label.toLowerCase() === activeTab ||
      tabs.SUBSCRIBED.label.toLowerCase() === activeTab
    )
  }

  const columns = [
    // {
    //   field: 'bodyType',
    //   headerName: ' ',
    //   flex: 0.5,
    //   renderCell: BodyStyleCell,
    // },
    {
      field: 'make',
      headerName: 'Make',
      flex: 0.5,
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 0.5,
    },
    {
      field: 'trim',
      headerName: 'Trim',
      flex: 1,
    },
    {
      field: 'miles',
      headerName: 'Miles',
      flex: 0.5,
    },
    {
      field: 'color',
      headerName: 'Color',
      flex: 0.5,
    },
    {
      field: 'vin',
      headerName: 'Vin',
      flex: 1,
    },
    {
      field: 'dealer',
      headerName: 'Dealership',
      flex: 1.2,
      renderCell(props: GridRenderCellParams) {
        return (
          <Tooltip title={props.row.dealerAddress}>
            <span>{props.row.dealerName}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'dealerAddress',
      headerName: 'Dealer Address',
      flex: 1.5,
      renderCell(props: GridRenderCellParams) {
        return (
          <Tooltip title={props.row.dealerAddress}>
            <span>{props.row.dealerAddress}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'list',
      headerName: 'List/Delist',
      flex: 1,
      renderCell: (props) => (
        <ListVehicleCell {...props} handler={listVehicle} />
      ),
    },
    {
      field: 'vehicleDetail',
      headerName: 'View Vehicle',
      flex: 1,
      renderCell: (props) => <VehicleDetailCell {...props} from="listings" />,
    },
  ]

  return (
    <Box>
      <Typography sx={{ marginLeft: '5px' }} variant="h1">
        Listings
      </Typography>
      {addVehicleMutation.isSuccess && (
        <Box>
          <Alert onClose={() => addVehicleMutation.reset()}>
            Vehicle added successfully.
          </Alert>
        </Box>
      )}
      {addMultipleVehiclesMutation.isSuccess && (
        <Box>
          <Alert onClose={() => addMultipleVehiclesMutation.reset()}>
            Multiple vehicles added successfully.
          </Alert>
        </Box>
      )}
      {addMultipleVehiclesMutation.isError && (
        <Box>
          <Alert severity="error" onClose={() => addMultipleVehiclesMutation.reset()}>
            {addMultipleVehiclesMutation.error instanceof Error
              ? addMultipleVehiclesMutation.error.message
              : 'Failed to add multiple vehicles'}
          </Alert>
        </Box>
      )}
      {listDelistMutation.isSuccess && (
        <Box>
          <Alert onClose={() => listDelistMutation.reset()}>
            Update Successful.
          </Alert>
        </Box>
      )}
      {listDelistMutation.isError && (
        <Box>
          <Alert severity="error" onClose={() => listDelistMutation.reset()}>
            {listDelistMutation.error?.message}
          </Alert>
        </Box>
      )}
      <Card>
        <Box sx={{ display: 'flex', alignItems: 'center', margin: '20px 10px' }}>
          <FilterButton
            isSelected={tabs.ELIGIBLE.label.toLowerCase() === activeTab}
            onClick={() => selectTab(tabs.ELIGIBLE.label)}
          >
            {tabs.ELIGIBLE.label}
          </FilterButton>
          <FilterButton
            isSelected={tabs.INELIGIBLE.label.toLowerCase() === activeTab}
            onClick={() => selectTab(tabs.INELIGIBLE.label)}
          >
            {tabs.INELIGIBLE.label}
          </FilterButton>
          <FilterButton
            isSelected={tabs.ASSIGNED.label.toLowerCase() === activeTab}
            onClick={() => selectTab(tabs.ASSIGNED.label)}
          >
            {tabs.ASSIGNED.label}
          </FilterButton>
          <FilterButton
            isSelected={tabs.SUBSCRIBED.label.toLowerCase() === activeTab}
            onClick={() => selectTab(tabs.SUBSCRIBED.label)}
          >
            {tabs.SUBSCRIBED.label}
          </FilterButton>
          <VehicleMakeSelectField
            onChange={(selectedMake) => setQuery({ make: selectedMake })}
            value={query.make}
          />
          <Box sx={{ marginLeft: 'auto', paddingRight: '16px', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setIsUploadMultipleDrawerOpen(true)}
            >
              Upload Multiple
            </Button>
            <Button
              variant="contained"
              onClick={() => setIsAddVehicleDrawerOpen(true)}
            >
              Add Vehicle
            </Button>
          </Box>
        </Box>
        {/* TODO: NEED TO CHECK WITH UX TEAM AND CHANGE THE WAY OF ERROR HANDLING */}
        {!fetchListVehiclesError && (
          <DataGrid
            rows={vehicles}
            rowCount={total}
            columns={columns}
            loading={vehicleQuery.isLoading || vehicleQuery.isFetching}
            columnVisibilityModel={{ list: !isHidden() }}
            disableColumnFilter
            paginationMode='server'
            pageSizeOptions={[25, 50, 100]}
            paginationModel={{page: query.page, pageSize: query.rowsPerPage}}
            onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
          />
        )}
        {!!vehicleToList.id && (
          <ListDelistModal
            open={!!vehicleToList.id}
            onClose={onListVehicleClose}
            listDelistMutation={listDelistMutation}
          />
        )}

        <FormDrawerComponent
          isDrawerOpen={isAddVehicleDrawerOpen}
          setIsDrawerOpen={setIsAddVehicleDrawerOpen}
          header="Add Vehicle"
          handleSave={handleAddVehicle}
          form={AddVehicleForm}
          defaultValues={{
            programId: '',
            financier: '',
            vin: '',
            make: '',
            model: '',
            trim: '',
            year: new Date().getFullYear(),
            exteriorColor: '',
            interiorColor: '',
            startingOdometerMiles: 0,
            additionalDetails: '',
          }}
        />

        <FormDrawerComponent
          isDrawerOpen={isUploadMultipleDrawerOpen}
          setIsDrawerOpen={setIsUploadMultipleDrawerOpen}
          header="Upload Multiple Vehicles"
          handleSave={handleUploadMultipleVehicles}
          form={UploadMultipleVehiclesForm}
          defaultValues={{
            programId: '',
            financier: '',
            vehicles: [],
          }}
        />
      </Card>
    </Box>
  )
}
